import { useMemo } from "react";

/**
 * Custom hook that generates lookup objects for Material Table column filters
 * Handles both text and array fields with proper null/empty value representations
 *
 * @param {Array} data - The data array to generate lookups from
 * @param {Object} fieldConfigs - Configuration for each field that needs a lookup
 *   Example: {
 *     aquifer_group: { type: 'text', field: 'aquifer_group' },
 *     management_zones: { type: 'array', field: 'management_zones' }
 *   }
 * @returns {Object} - Object with lookup objects for each field
 */
export const useTableFilterLookups = (data, fieldConfigs) => {
  return useMemo(() => {
    const lookups = {};

    Object.entries(fieldConfigs).forEach(([key, config]) => {
      const { type, field } = config;
      const lookup = {};

      if (type === "text") {
        // For text fields
        data.forEach((row) => {
          const value = row[field];
          if (value === "<blank>") {
            lookup["<blank>"] = "<blank>";
          } else if (value) {
            lookup[value] = value;
          }
        });
      } else if (type === "array") {
        // For array fields that have been transformed to strings
        data.forEach((row) => {
          const value = row[field];
          if (value === "<empty>") {
            lookup["<empty>"] = "<empty>";
          } else if (typeof value === "string" && value) {
            // For transformed arrays that are now comma-separated strings
            // We want the whole string as the lookup value
            lookup[value] = value;
          }
        });
      }

      lookups[key] = lookup;
    });

    return lookups;
  }, [data, fieldConfigs]);
};

/**
 * Custom hook that generates lookup objects for Material Table column filters
 * from raw data before transformation
 *
 * @param {Array} rawData - The raw data array before transformation
 * @param {Object} fieldConfigs - Configuration for each field that needs a lookup
 *   Example: {
 *     aquifer_group: { type: 'text', field: 'aquifer_group' },
 *     management_zones: { type: 'array', field: 'management_zones' }
 *   }
 * @returns {Object} - Object with lookup objects for each field
 */
export const useTableFilterLookupsFromRaw = (rawData, fieldConfigs) => {
  return useMemo(() => {
    const lookups = {};

    Object.entries(fieldConfigs).forEach(([key, config]) => {
      const { type, field } = config;
      const lookup = {};
      const valueSet = new Set();
      let hasNull = false;
      let hasEmpty = false;

      if (type === "text") {
        // For text fields
        rawData.forEach((row) => {
          const value = row[field];
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            (typeof value === "string" && value.trim() === "")
          ) {
            hasNull = true;
          } else {
            valueSet.add(value);
          }
        });

        // Add all unique values to lookup
        valueSet.forEach((value) => {
          lookup[value] = value;
        });

        // Add <blank> if there were null values
        if (hasNull) {
          lookup["<blank>"] = "<blank>";
        }
      } else if (type === "array") {
        // For array fields
        rawData.forEach((row) => {
          const value = row[field];

          if (
            value === null ||
            value === undefined ||
            (Array.isArray(value) &&
              (value.length === 0 || (value.length === 1 && value[0] === null)))
          ) {
            hasEmpty = true;
          } else if (Array.isArray(value)) {
            value.forEach((item) => {
              if (item !== null && item !== undefined) {
                valueSet.add(item);
              }
            });
          }
        });

        // Add all unique values to lookup
        valueSet.forEach((value) => {
          lookup[value] = value;
        });

        // Add <empty> if there were empty arrays
        if (hasEmpty) {
          lookup["<empty>"] = "<empty>";
        }
      }

      lookups[key] = lookup;
    });

    return lookups;
  }, [rawData, fieldConfigs]);
};
